/**
 * Apply and Apply all of the suggestions card (plan 7.4): one manual op for
 * one suggestion, one batch for all of them, never a model call.
 *
 * - With the panel editing this artifact on the suggestions' version, the
 *   ops go through the editor (they show at once and save in its session).
 * - Otherwise the card saves them itself: `page-edits` with the ops, or the
 *   document with the suggestions applied through `PUT document`, marked
 *   `via: 'suggestion'` for analytics, on the version the set names now.
 * - A 409 means the file changed under the suggestions: the rest read as
 *   stale ("Ask again for new ones").
 */
import type { ArtifactDocument, PageEditOp, PageEditsResult } from '@tabbio/types';
import type { ArtifactDocumentSaveResult } from '@/lib/artifact-edits-api';
import type { ActiveEditor } from '../editor/active-editor';
import { classifySaveError } from '../editor/save-errors';
import {
  applyDocumentSuggestions,
  pageSuggestionOp,
} from './suggestions-model';
import {
  notePageSave,
  noteDocumentSave,
  readSuggestionSet,
  setSuggestionStatus,
  type SuggestionItem,
} from './suggestions-store';

export type SuggestionsDeps = {
  activeEditor: (artifactId: string) => ActiveEditor | null;
  savePageEdits: (
    artifactId: string,
    body: { baseVersionId: string; editSessionId: string; ops: PageEditOp[]; via: 'suggestion' },
  ) => Promise<PageEditsResult>;
  /** The document of a version (`GET versions/:versionId`). */
  loadDocument: (artifactId: string, versionId: string) => Promise<ArtifactDocument | null>;
  saveDocument: (
    artifactId: string,
    body: { baseVersionId: string; editSessionId: string; document: ArtifactDocument; via: 'suggestion' },
  ) => Promise<ArtifactDocumentSaveResult>;
  /** The artifact changed: refresh its card, panel and versions. */
  onChanged: (artifactId: string) => void;
};

export type ApplyOutcome = 'applied' | 'stale' | 'failed' | 'unsupported' | 'none';

function pendingItems(key: string, indexes: readonly number[] | 'all'): SuggestionItem[] {
  const set = readSuggestionSet(key);
  if (!set) return [];
  return set.items.filter(
    (item) => (item.status === 'pending' || item.status === 'failed') && (indexes === 'all' || indexes.includes(item.index)),
  );
}

/** Applies the pending suggestions `indexes` of the card `key` (or all of them). */
export async function applySuggestions(
  key: string,
  indexes: readonly number[] | 'all',
  deps: SuggestionsDeps,
): Promise<ApplyOutcome> {
  const set = readSuggestionSet(key);
  const items = pendingItems(key, indexes);
  if (!set || items.length === 0) return 'none';
  const chosen = items.map((item) => item.index);
  const editor = deps.activeEditor(set.artifactId);
  const inEditor = editor && editor.baseVersionId() === set.versionId ? editor : null;

  if (set.kind === 'page') {
    const ops = items.map(pageSuggestionOp).filter((op): op is PageEditOp => op !== null);
    if (ops.length === 0) {
      setSuggestionStatus(key, chosen, 'unsupported');
      return 'unsupported';
    }
    if (inEditor?.applyPageOps) {
      inEditor.applyPageOps(ops);
      setSuggestionStatus(key, chosen, 'applied');
      return 'applied';
    }
    setSuggestionStatus(key, chosen, 'applying');
    try {
      const base = set.versionId;
      const result = await deps.savePageEdits(set.artifactId, {
        baseVersionId: base,
        editSessionId: set.editSessionId,
        ops,
        via: 'suggestion',
      });
      setSuggestionStatus(key, chosen, 'applied');
      notePageSave(set.artifactId, base, result.version.id, result.ids);
      deps.onChanged(set.artifactId);
      return 'applied';
    } catch (error) {
      return failed(key, chosen, error);
    }
  }

  if (set.kind === 'document' || set.kind === 'slides') {
    if (inEditor?.applyDocument && inEditor.document) {
      const current = inEditor.document();
      if (!current) return 'none';
      const result = applyDocumentSuggestions(current, items, set.kind);
      if (result.applied.length) {
        inEditor.applyDocument(() => result.document);
        setSuggestionStatus(key, result.applied.map((at) => items[at].index), 'applied');
      }
      for (const refusal of result.refused) setSuggestionStatus(key, [items[refusal.index].index], refusal.reason);
      return result.applied.length ? 'applied' : result.refused[0]?.reason ?? 'none';
    }
    setSuggestionStatus(key, chosen, 'applying');
    try {
      const base = set.versionId;
      const document = await deps.loadDocument(set.artifactId, base);
      if (!document) {
        setSuggestionStatus(key, chosen, 'stale');
        return 'stale';
      }
      const result = applyDocumentSuggestions(document, items, set.kind);
      for (const refusal of result.refused) setSuggestionStatus(key, [items[refusal.index].index], refusal.reason);
      if (!result.applied.length) return result.refused[0]?.reason ?? 'none';
      const saved = await deps.saveDocument(set.artifactId, {
        baseVersionId: base,
        editSessionId: set.editSessionId,
        document: result.document,
        via: 'suggestion',
      });
      setSuggestionStatus(key, result.applied.map((at) => items[at].index), 'applied');
      noteDocumentSave(set.artifactId, base, saved.version.id);
      deps.onChanged(set.artifactId);
      return 'applied';
    } catch (error) {
      return failed(key, chosen, error);
    }
  }

  setSuggestionStatus(key, chosen, 'unsupported');
  return 'unsupported';
}

function failed(key: string, indexes: number[], error: unknown): ApplyOutcome {
  const failure = classifySaveError(error);
  if (failure.kind === 'conflict' || failure.kind === 'gone') {
    const set = readSuggestionSet(key);
    // Every suggestion still waiting was made on a version that is gone.
    const waiting = set?.items.filter((item) => item.status === 'pending' || item.status === 'applying' || item.status === 'failed') ?? [];
    setSuggestionStatus(key, waiting.map((item) => item.index), 'stale');
    return 'stale';
  }
  setSuggestionStatus(key, indexes, 'failed');
  return 'failed';
}

export function dismissSuggestion(key: string, index: number) {
  setSuggestionStatus(key, [index], 'dismissed');
}
